import { api, errorMessage } from '@minirag/api';
import { Loader2 } from 'lucide-react';
import { useState, type SubmitEvent } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';

import { BrandMark, BrandName } from '../../components/Brand';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input, Label } from '../../components/ui/input';
import { ThemeToggle } from '../theme/ThemeToggle';
import { useAuth } from './AuthContext';

/**
 * Where an invite email's link lands. No session exists yet — this calls
 * `/v1/auth/accept-invite` directly rather than going through `useAuth`,
 * then hands the returned session to the auth context the same way login does.
 */
export function AcceptInvitePage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const { session, adoptSession } = useAuth();

  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  if (session) return <Navigate to="/" replace />;

  if (!token) {
    return (
      <div className="surface-grid grid min-h-screen place-items-center px-4">
        <Card className="w-full max-w-sm">
          <CardContent className="text-center">
            <p className="font-medium">This invite link is missing its token.</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Ask whoever invited you to send a new one.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  async function handleSubmit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    setError('');

    if (password !== confirm) {
      setError('Passwords do not match');
      return;
    }

    setBusy(true);
    try {
      const { data, error } = await api.POST('/v1/auth/accept-invite', {
        body: { token, password, display_name: displayName },
      });
      if (error || !data) {
        throw new Error(errorMessage(error, 'Could not accept this invite'));
      }
      adoptSession(data);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="surface-grid grid min-h-screen place-items-center px-4 py-12">
      <div className="absolute top-4 right-4">
        <ThemeToggle />
      </div>

      <div className="w-full max-w-sm">
        <div className="mb-8 flex flex-col items-center gap-3 text-center">
          <BrandMark className="size-12 shadow-[0_0_28px_-6px_var(--brand-via)]" />
          <div>
            <h1>
              <BrandName className="text-2xl" />
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">Set up your account to join.</p>
          </div>
        </div>

        <Card>
          <CardContent>
            <form className="grid gap-4" onSubmit={handleSubmit} noValidate>
              <div className="grid gap-1.5">
                <Label htmlFor="display-name">Your name</Label>
                <Input
                  id="display-name"
                  autoFocus
                  autoComplete="name"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                />
              </div>

              <div className="grid gap-1.5">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>

              <div className="grid gap-1.5">
                <Label htmlFor="confirm">Confirm password</Label>
                <Input
                  id="confirm"
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={8}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                />
              </div>

              {error && (
                <p
                  role="alert"
                  className="rounded-[var(--radius-control)] bg-danger-muted px-3 py-2 text-sm text-danger"
                >
                  {error}
                </p>
              )}

              <Button type="submit" disabled={busy} className="mt-1 w-full">
                {busy && <Loader2 className="animate-spin" />}
                {busy ? 'Joining' : 'Join the team'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
